import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import api from '../services/api';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  backdrop-filter: blur(2px);
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${(props) =>
    props.variant === 'primary'
      ? `
    background: linear-gradient(135deg, #043663 0%, #043663 100%);
    color: white;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #032a4f 0%, #032a4f 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
  `
      : `
    background: #f3f4f6;
    color: #374151;

    &:hover:not(:disabled) {
      background: #e5e7eb;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const Message = styled.div`
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
  margin-bottom: 1rem;

  ${(props) =>
    props.type === 'success'
      ? `
    background-color: #d1fae5;
    color: #065f46;
    border: 1px solid #a7f3d0;
  `
      : props.type === 'error'
        ? `
    background-color: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
  `
        : `
    background-color: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
  `}
`;

const Hint = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
`;

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, step]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) handleClose();
    };
    if (isOpen) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    setStep('email');
    setEmail('');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setMessage(null);
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      setMessage({ type: 'error', text: 'Please enter your player email address' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const response = await api.post('/password-reset/forgot-password', { email: email.trim() });
      if (response && response.success) {
        setMessage({
          type: 'success',
          text: response.message || 'If an account exists, we sent a 6-digit code.',
        });
        setStep('otp');
      } else {
        setMessage({ type: 'error', text: response?.message || 'An error occurred' });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        (error.response?.status === 429
          ? 'Too many requests. Please wait before trying again.'
          : 'Could not send the code. Please try again.');
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      setMessage({ type: 'error', text: 'Enter the 6-digit code from your email' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const response = await api.post('/password-reset/verify-otp', {
        email: email.trim(),
        code: code.trim(),
      });
      if (response && response.success) {
        setMessage({ type: 'success', text: 'Code verified. Choose a new password.' });
        setStep('password');
      } else {
        setMessage({ type: 'error', text: response?.message || 'Invalid or expired code' });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Invalid or expired code',
      });
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const response = await api.post('/password-reset/reset-password-otp', {
        email: email.trim(),
        code: code.trim(),
        newPassword,
      });
      if (response && response.success) {
        setMessage({
          type: 'success',
          text: 'Password updated. You can sign in with your new password.',
        });
        setTimeout(handleClose, 1800);
      } else {
        setMessage({ type: 'error', text: response?.message || 'Could not reset password' });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Could not reset password',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Forgot Password</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        {message && <Message type={message.type}>{message.text}</Message>}

        {step === 'email' && (
          <Form onSubmit={requestCode}>
            <FormGroup>
              <Label htmlFor="resetEmail">Player email</Label>
              <Input
                ref={inputRef}
                type="email"
                id="resetEmail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                disabled={loading}
              />
            </FormGroup>
            <Hint>
              We will email a 6-digit code to your player account. Team name is not used for reset.
            </Hint>
            <ButtonGroup>
              <Button type="button" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Sending…' : 'Send code'}
              </Button>
            </ButtonGroup>
          </Form>
        )}

        {step === 'otp' && (
          <Form onSubmit={verifyCode}>
            <FormGroup>
              <Label htmlFor="resetCode">6-digit code</Label>
              <Input
                ref={inputRef}
                type="text"
                id="resetCode"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                required
                disabled={loading}
              />
            </FormGroup>
            <Hint>Enter the code sent to {email}. It expires in 15 minutes.</Hint>
            <ButtonGroup>
              <Button
                type="button"
                onClick={() => {
                  setStep('email');
                  setMessage(null);
                }}
                disabled={loading}
              >
                Back
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Checking…' : 'Verify code'}
              </Button>
            </ButtonGroup>
          </Form>
        )}

        {step === 'password' && (
          <Form onSubmit={submitNewPassword}>
            <FormGroup>
              <Label htmlFor="resetNewPassword">New password</Label>
              <Input
                ref={inputRef}
                type="password"
                id="resetNewPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                required
                disabled={loading}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="resetConfirmPassword">Confirm password</Label>
              <Input
                type="password"
                id="resetConfirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
                required
                disabled={loading}
              />
            </FormGroup>
            <ButtonGroup>
              <Button
                type="button"
                onClick={() => {
                  setStep('otp');
                  setMessage(null);
                }}
                disabled={loading}
              >
                Back
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Saving…' : 'Set new password'}
              </Button>
            </ButtonGroup>
          </Form>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default ForgotPasswordModal;
